import { OrderDetails } from "../interface/interfaces";
import { useAuth } from "../context/AuthContext";
import { useEffect, useState } from "react";
import {
  addToCartAPi,
  clearAllCart,
  deleteFromCartApi,
  loadCart,
  updateCartApi,
} from "../api/requests";
import { useNotification } from "../context/NotificationContext";
import { useAnalytics } from "./useAnalytics";

export function useCartLogic() {
  const { isLoggedIn, authVersion } = useAuth();
  const [cartData, setCartData] = useState<OrderDetails[]>([]);
  const [isCartLoading, setCartLoading] = useState(false);
  const { notify } = useNotification();
  const { trackAddToCart, trackRemoveFromCart, trackViewCart } = useAnalytics();

  const getCart = async () => {
    if (isLoggedIn()) {
      try {
        // setCartLoading(true);
        let { cartItems } = await loadCart(1, 100);

        return cartItems;
      } catch (error: any) {
        notify.error(error.toString());
      } finally {
        // setCartLoading(false);
      }
    } else {
      const stored = localStorage.getItem("cart_products");
      const existing_cart: OrderDetails[] = stored ? JSON.parse(stored) : [];
      return existing_cart;
    }
  };

  const syncCart = async (isLoader: boolean) => {
    try {
      if (isLoader) setCartLoading(true);

      const cart = await getCart();
      if (cart) {
        setCartData(cart);
        // Track cart view when cart is loaded
        if (cart.length > 0) {
          await trackViewCart(cart);
        }
      }
    } catch (error) {
    } finally {
      if (isLoader) setCartLoading(false);
    }
  };

  useEffect(() => {
    const func = async () => {
      await syncCart(true);
    };
    func();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const func = async () => {
      if (authVersion > 0) {
        await syncCart(true);
      }
    };
    func();
    // eslint-disable-next-line
  }, [authVersion]);

  const addToCart = async (orderData: OrderDetails) => {
    try {
      if (isLoggedIn()) {
        // TODO: Add to cart on backend
        let size = "",
          color = "";

        let sizeProp = orderData.selectedProperties?.find((data) => {
          return data.type === "size";
        });

        if (sizeProp) {
          size = sizeProp.value;
        }

        let colorProp = orderData.selectedProperties?.find((data) => {
          return data.type === "color";
        });

        if (colorProp) {
          color = colorProp.value;
        }

        await addToCartAPi(
          orderData.productId,
          size,
          color,
          orderData.quantity
        );
      } else {
        const stored = localStorage.getItem("cart_products");
        const existing_cart: OrderDetails[] = stored ? JSON.parse(stored) : [];

        const updatedCart = [...existing_cart, orderData];
        localStorage.setItem("cart_products", JSON.stringify(updatedCart));
        setCartData(updatedCart);
      }

      // Track add to cart event
      await trackAddToCart(orderData);

      await syncCart(false);

      return true;
    } catch (error: any) {
      notify.error(error.toString());
      return false;
    }
  };

  const setOrderLoader = (value: boolean, id: string) => {
    setCartData((prev) => {
      const updatedCart = prev.map((item) => {
        if (item.id === id) {
          return { ...item, isUpdating: value };
        }
        return item;
      });

      return updatedCart;
    });
  };

  const deleteFromCart = async (id: string) => {
    try {
      setOrderLoader(true, id);

      // Find the item being removed for analytics
      const itemToRemove = cartData.find(item => item.id === id);

      if (isLoggedIn()) {
        await deleteFromCartApi(id);
      } else {
        const stored = localStorage.getItem("cart_products");
        const existing_cart: OrderDetails[] = stored ? JSON.parse(stored) : [];

        const newCart = existing_cart.filter((data) => data.id !== id);
        localStorage.setItem("cart_products", JSON.stringify(newCart));
        setCartData(newCart);
      }

      // Track remove from cart event
      if (itemToRemove) {
        await trackRemoveFromCart(itemToRemove);
      }

      await syncCart(false);
      return true;
    } catch (error: any) {
      notify.error(error.toString());
      return false;
    } finally {
      setOrderLoader(false, id);
    }
  };

  const clearCart = async () => {
    try {
      setCartLoading(true);

      if (isLoggedIn()) {
        await clearAllCart();
      } else {
        localStorage.setItem("cart_products", JSON.stringify([]));
        setCartData([]);
      }

      await syncCart(false);
      return true;
    } catch (error: any) {
      notify.error(error.toString());
      return false;
    } finally {
      setCartLoading(false);
    }
  };

  const updateOrder = async (order: OrderDetails) => {
    let successResp: OrderDetails[] = [];

    try {
      if (isLoggedIn()) {
        // TODO: Update order in backend cart

        setCartData((prev) => {
          const updatedCart = prev.map((item) =>
            item.id === order.id ? order : item
          );

          return updatedCart;
        });

        let color = "",
          size = "";

        let colorProp = order.selectedProperties?.find((data) => {
          return data.type === "color";
        });

        if (colorProp) {
          color = colorProp.value;
        }

        let sizeProp = order.selectedProperties?.find((data) => {
          return data.type === "size";
        });

        if (sizeProp) {
          size = sizeProp.value;
        }

        successResp = await updateCartApi(
          order.id,
          order.quantity,
          size,
          color
        );
      } else {
        const stored = localStorage.getItem("cart_products");
        const existing_cart: OrderDetails[] = stored ? JSON.parse(stored) : [];

        const updatedCart = existing_cart.map((item) =>
          item.id === order.id ? order : item
        );

        localStorage.setItem("cart_products", JSON.stringify(updatedCart));
        setCartData(updatedCart);
      }

      return true;
    } catch (error: any) {
      setCartData(successResp);
      notify.error(error.toString());
      return false;
    }
  };

  return {
    addToCart,
    deleteFromCart,
    clearCart,
    updateOrder,
    cartData,
    isCartLoading,
    syncCart
  };
}
